import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTheme } from "../../../providers/ThemeContext";
import { useI18n } from "../../../languages/providers/I18nProvider";
import { useMediaQuery } from "@mui/material";

import { ourworks, ourworkKeys } from "../OurWorkContent";
import { GlobalStyles } from "../../../styles/GlobalStyles";
import DetailedCard from "../../../components/cards/DetailedCard/DetailedCard";
import { OurWorkDetailsStyles } from "./OurWorkDetailsStyles";
import FaqAccordion from "../../../components/common/FaqAccordion";
import { breadcrumbSchema } from "../../../config/seoConfig";
import SEO from "../../../components/common/SEO";
import { useLangPath } from "../../../hooks/useLangPath";
import NotFoundPage from "../../notFound/Notfoundpage";

type OurWorkItem = {
  id: string;
  cat: string;
  year: number | string;
  titleKey: string;
  locationKey: string;
  image?: string;
  blurbKey: string;
  descriptionKey?: string; // long description (optional)
  subtitleKey?: string; // shorter description (optional)
  pointsKeys?: string[]; // i18n keys for specs (optional)
  points?: string[]; // plain strings for specs (optional)
  metrics?: { labelKey: string; value: string | number }[];
  faq?: { qKey: string; aKey: string }[];
};

// Per-project SEO title/description. Canonical URLs are handled by the SEO
// component from the current path, so they are not set here.
const ourworkSeoConfig: Record<
  string,
  { titleKey: string; descriptionKey: string }
> = {
  "cairo-flagpole-flag": {
    titleKey: "seo:ourwork:projects:cairo-flagpole:title",
    descriptionKey: "seo:ourwork:projects:cairo-flagpole:description",
  },
  "highway-flag-campaign": {
    titleKey: "seo:ourwork:projects:highway-flag-campaign:title",
    descriptionKey: "seo:ourwork:projects:highway-flag-campaign:description",
  },
  "high-speed-printing": {
    titleKey: "seo:ourwork:projects:high-speed-printing:title",
    descriptionKey: "seo:ourwork:projects:high-speed-printing:description",
  },
  "24hr-order-fulfilment": {
    titleKey: "seo:ourwork:projects:24hr-order-fulfilment:title",
    descriptionKey: "seo:ourwork:projects:24hr-order-fulfilment:description",
  },
  "jeddah-flagpole-flag": {
    titleKey: "seo:ourwork:projects:jeddah-flagpole:title",
    descriptionKey: "seo:ourwork:projects:jeddah-flagpole:description",
  },
};

const OurWorkDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const { t, lang } = useI18n();
  const isMobile = useMediaQuery("(max-width: 900px)");

  const g = GlobalStyles(theme);
  const s = OurWorkDetailsStyles(theme, isMobile);
  const lp = useLangPath();

  const item = useMemo<OurWorkItem | undefined>(
    () => ourworks.find((a) => a.id === id) as OurWorkItem | undefined,
    [id],
  );

  // Unknown project id → real 404 (all hooks above run first)
  if (!item) return <NotFoundPage />;

  // ---- Description logic (long -> subtitle -> blurb) ----
  const descriptionKey =
    item.descriptionKey ?? item.subtitleKey ?? item.blurbKey;
  const description = descriptionKey ? t(descriptionKey) : undefined;

  // ---- Optional "Specifications" ----
  // Prefer i18n keys if present; otherwise take raw strings.
  const specItems: string[] =
    (item.pointsKeys && item.pointsKeys.length
      ? item.pointsKeys.map((k) => t(k))
      : item.points) || [];

  const hasSpecs = specItems.length > 0;

  // t() returns the key itself when a translation is missing
  const specsKey = "ourwork:labels:specs";
  const specsTitle = t(specsKey) !== specsKey ? t(specsKey) : "Specifications";

  const related = ourworks
    .filter((a) => a.id !== item.id && a.cat === item.cat)
    .slice(0, 3);

  const seo = ourworkSeoConfig[item.id];

  return (
    <main style={g.page}>
      <SEO
        title={seo ? t(seo.titleKey) : `${t(item.titleKey)} | Creation Arts`}
        description={seo ? t(seo.descriptionKey) : t(item.blurbKey)}
        lang={lang}
        schema={[
          ...(item.faq?.length
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: item.faq.map((it) => ({
                    "@type": "Question",
                    name: t(it.qKey),
                    acceptedAnswer: { "@type": "Answer", text: t(it.aKey) },
                  })),
                },
              ]
            : []),
          breadcrumbSchema(lang, [
            { name: t("nav:home"), path: "" },
            { name: t("nav:ourwork"), path: "/ourwork" },
            { name: t(item.titleKey), path: `/ourwork/${item.id}` },
          ]),
        ]}
      />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" style={s.breadcrumbWrap}>
        <Link to={lp("/ourwork")} style={s.backLink}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{t(ourworkKeys.pageTitle)}</span>
        </Link>
      </nav>

      {/* Hero */}
      <section style={s.hero}>
        <div style={s.heroInner}>
          <header>
            <h1 style={s.title}>{t(item.titleKey)}</h1>

            <div style={s.metaRow}>
              <span>
                {t(ourworkKeys.labels.year)}: <strong>{item.year}</strong>
              </span>
              <span aria-hidden="true" style={s.metaDot}>
                •
              </span>
              <span>
                {t(ourworkKeys.labels.location)}:{" "}
                <strong>{t(item.locationKey)}</strong>
              </span>
            </div>

            {description ? <p style={s.lead}>{description}</p> : null}

            {item.metrics?.length ? (
              <div style={s.statsRow}>
                {item.metrics.map((m, i) => (
                  <div key={i} style={s.statPill}>
                    <span>{t(m.labelKey)}:</span>
                    <span>{m.value}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </header>

          {item.image ? (
            <div style={s.mediaBox}>
              {/* Main image is above the fold: load it immediately (better LCP) */}
              <img
                src={item.image}
                alt={t(item.titleKey)}
                loading="eager"
                decoding="async"
                style={s.media}
              />
            </div>
          ) : null}
        </div>
      </section>

      {/* Optional Specifications */}
      {hasSpecs ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{specsTitle}</h2>
          <ul
            style={{
              listStyle: "disc",
              paddingInlineStart: 20,
              lineHeight: 1.65,
              marginTop: 8,
              opacity: 0.95,
              fontSize: 15,
            }}
          >
            {specItems.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* FAQ */}
      {item.faq?.length ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("ourwork:labels:faq")}</h2>
          <FaqAccordion
            items={item.faq.map((it) => ({
              q: t(it.qKey),
              a: t(it.aKey),
            }))}
            defaultOpen={0}
          />
        </section>
      ) : null}

      {/* Related */}
      {related.length ? (
        <section style={s.section}>
          <h2 style={s.sectionTitle}>{t("ourwork:filters:" + item.cat)}</h2>
          <div style={g.cardGrid}>
            {related.map((r) => (
              <DetailedCard
                key={r.id}
                title={t(r.titleKey)}
                image={r.image ?? undefined}
                yearLabel={t(ourworkKeys.labels.year)}
                year={r.year}
                locationLabel={t(ourworkKeys.labels.location)}
                location={t(r.locationKey)}
                blurb={t(r.blurbKey)}
                metrics={(r.metrics ?? []).map((m) => ({
                  label: t(m.labelKey),
                  value: m.value,
                }))}
                to={lp(`/ourwork/${r.id}`)}
              />
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
};

export default OurWorkDetailsPage;
